<template>
  <div v-if="loaded" class="branding-special-field mt-6">
    <div v-html="specialFieldContent" class="mb-5" />

    <div class="branding-columns">
      <b-field class="file-upload">
        <template #label>
          {{ $t("mp:self_onboarding.branding.input.compact_logo.label") }}
          <b-tooltip
            type="is-dark"
            :label="
              $t('mp:self_onboarding.branding.input.compact_logo.tooltip')
            "
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-upload
          v-model="fields.compactLogo"
          @input="onBlur"
          accept="image/*"
          drag-drop
          expanded
        >
          <section v-if="fields.compactLogo" class="logo-preview">
            <img :src="getLogoPreview(fields.compactLogo)" />
          </section>
          <section v-else-if="logos.compactLogo" class="logo-preview">
            <img :src="getLogoPreview(logos.compactLogo)" />
          </section>
          <section v-else class="section">
            <div class="content has-text-centered">
              <p>
                <b-icon icon="upload" size="is-large"></b-icon>
              </p>
            </div>
          </section>
        </b-upload>
      </b-field>
      <b-field class="file-upload">
        <template #label>
          {{ $t("mp:self_onboarding.branding.input.big_logo.label") }}
          <b-tooltip
            type="is-dark"
            :label="$t('mp:self_onboarding.branding.input.big_logo.tooltip')"
          >
            <b-icon size="is-small" icon="info-circle"></b-icon>
          </b-tooltip>
        </template>
        <b-upload
          v-model="fields.bigLogo"
          @input="onBlur"
          accept="image/*"
          drag-drop
          expanded
        >
          <section v-if="fields.bigLogo" class="logo-preview">
            <img :src="getLogoPreview(fields.bigLogo)" />
          </section>
          <section v-else-if="logos.bigLogo" class="logo-preview">
            <img :src="getLogoPreview(logos.bigLogo)" />
          </section>
          <section v-else class="section">
            <div class="content has-text-centered">
              <p>
                <b-icon icon="upload" size="is-large"></b-icon>
              </p>
            </div>
          </section>
        </b-upload>
      </b-field>
    </div>

    <ColorPicker
      v-model="fields.brandingPrimary"
      :label="$t('mp:self_onboarding.branding.input.branding_primary.label')"
      :tooltip="
        $t('mp:self_onboarding.branding.input.branding_primary.tooltip')
      "
      @blur="onBlur"
    />

    <ColorPicker
      v-model="fields.brandingSecondary"
      :label="$t('mp:self_onboarding.branding.input.branding_secondary.label')"
      :tooltip="
        $t('mp:self_onboarding.branding.input.branding_secondary.tooltip')
      "
      @blur="onBlur"
    />
  </div>
  <div v-else>
    <div class="branding-columns">
      <span><b-skeleton height="94px" /></span>
      <span><b-skeleton height="94px" /></span>
    </div>
    <b-field>
      <b-skeleton height="40px" />
    </b-field>
    <b-field>
      <b-skeleton height="40px" />
    </b-field>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "BrandingSpecialField",
  components: {
    ColorPicker: () => import("@/components/ColorPicker"),
  },
  props: {
    specialFieldContent: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      loaded: false,
      applicationVisiblePoll: null,
      isPurchaseListEmpty: true,
      fields: {
        compactLogo: null, // private MultipartFile
        bigLogo: null, // private MultipartFile
        brandingPrimary: "#000000", // private String
        brandingSecondary: "#000000", // private String
      },
      logos: {
        compactLogo: null,
        bigLogo: null,
      },
      formData: new FormData(),
    };
  },
  watch: {
    fields: {
      async handler(fields) {
        for (const [key, value] of Object.entries(fields)) {
          if (value) {
            this.formData.set(key, value);
          }
        }
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters({
      branding: "onboarding/branding",
    }),
  },
  methods: {
    ...mapActions({
      getMyBranding: "onboarding/getMyBranding",
      putBranding: "onboarding/putBranding",
      downloadFile: "onboarding/downloadFile",
    }),
    onBlur() {
      this.putBranding(this.formData);
    },
    getLogoPreview(file) {
      if (file) {
        return URL.createObjectURL(file);
      }
    },
  },
  async created() {
    await this.getMyBranding();
    this.fields = { ...this.fields, ...this.branding };
    if (!Object.entries(this.branding).length) {
      this.putBranding(this.formData);
    }
    if (this.branding.bigLogo) {
      this.logos.bigLogo = await this.downloadFile(this.branding.bigLogo);
      this.fields.bigLogo = null;
      this.formData.delete("bigLogo");
    }
    if (this.branding.compactLogo) {
      this.logos.compactLogo = await this.downloadFile(
        this.branding.compactLogo
      );
      this.fields.compactLogo = null;
      this.formData.delete("compactLogo");
    }
    this.loaded = true;
  },
};
</script>

<style lang="scss" scoped>
.field {
  margin-bottom: 3rem;
}

.file-upload {
  position: relative;
  box-sizing: content-box;
  width: 100%;
  height: 119px;

  .upload.control {
    padding: 0 !important;
    width: 100%;
    height: calc(100% - 24px);
  }

  .logo-preview {
    border-radius: 4px;
    width: 100%;
    height: 100%;
    overflow: hidden;

    .delete {
      position: absolute;
      top: 8px;
      right: 8px;
    }

    img {
      border-radius: inherit;
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
}

.branding-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 2rem;

  .field {
    margin-bottom: 0;
  }
}
</style>

<style lang="scss">
input[type="color"] {
  // border: 1px solid red;
  padding: 2px;
  width: 50px;
}
.branding-special-field {
  & > .field {
    margin-bottom: 2rem;
  }

  .label {
    display: flex;
    gap: 4px;
    align-items: center;
    margin-bottom: 4px;

    .tooltip-trigger {
      height: 16px;
    }

    .b-tooltip {
      height: 16px;
    }
  }
}
</style>
